import React, { useState, useEffect, useCallback, useRef } from "react"; // Added useRef
import {
  Table,
  Button,
  Input,
  Space,
  Modal,
  message,
  Tag,
  Popconfirm,
  Tooltip,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext";
import {
  getUsers,
  addUser,
  updateUser,
  deleteUser,
} from "../../services/userService";
import UserForm from "../../components/users/UserForm";

const { Search } = Input;

// --- Constants ---
const DEBOUNCE_DELAY = 500; // milliseconds delay for search input

// Role rendering (keep as is)
const getRoleTag = (role) => {
  let color = "default";
  if (role === "administrator") color = "volcano";
  if (role === "manager") color = "blue";
  if (role === "viewer") color = "geekblue";
  const roleLabel = typeof role === "string" ? role.toUpperCase() : "UNKNOWN";
  return (
    <Tag color={color}>
      <SafetyCertificateOutlined style={{ marginRight: 4 }} />
      {roleLabel}
    </Tag>
  );
};

const UserListPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [sorter, setSorter] = useState({});
  const [searchTerm, setSearchTerm] = useState(""); // User's current input
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(""); // Debounced value for fetching
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const debounceTimeoutRef = useRef(null); // Ref to store debounce timeout ID

  const { isAdmin, user: currentUser } = useAuth();

  // --- Fetch Data Logic ---
  // Memoize fetchData. It now depends on `isAdmin`.
  const fetchData = useCallback(
    async (fetchParams) => {
      if (!isAdmin) {
        setUsers([]);
        setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
        return;
      }

      console.log("Fetching users with params:", fetchParams);
      setLoading(true);
      try {
        // Use the search term provided in fetchParams, which will be the debounced term
        const queryParams = {
          page: fetchParams.current,
          pageSize: fetchParams.pageSize,
          search: fetchParams.search, // Use the search term passed in
          sortField: fetchParams.sorter?.field,
          sortOrder: fetchParams.sorter?.order,
        };

        const result = await getUsers(queryParams);
        setUsers(result.data);
        setPagination((prev) => ({
          ...prev,
          current: queryParams.page,
          pageSize: queryParams.pageSize,
          total: result.total,
        }));
      } catch (error) {
        message.error(`Failed to fetch users: ${error.message}`);
        console.error("Fetch users error:", error);
      } finally {
        setLoading(false);
        console.log("Fetching finished.");
      }
    },
    [isAdmin]
  ); // Only depends on isAdmin

  // --- Effects ---

  // Effect for initial data load (based on isAdmin)
  useEffect(() => {
    if (isAdmin) {
      fetchData({
        current: 1, // Start at page 1 initially
        pageSize: pagination.pageSize,
        search: "", // Initial search is empty
        sorter: {},
      });
    } else {
      setUsers([]);
      setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]); // Run only when admin status changes (or on mount)

  // Effect for Debouncing Search Term
  useEffect(() => {
    // Clear the previous timeout if searchTerm changes
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set a new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm); // Update the debounced term after delay
    }, DEBOUNCE_DELAY);

    // Cleanup function to clear timeout if component unmounts or searchTerm changes again
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm]); // This effect runs whenever the raw searchTerm changes

  // Effect to Fetch Data when Debounced Search Term Changes
  useEffect(() => {
    // Don't fetch on initial load if debouncedSearchTerm is empty and pagination is at default
    // This prevents double fetch on mount if initial searchTerm is empty.
    // The initial fetch is handled by the `isAdmin` effect.
    if (
      pagination.current === 1 &&
      pagination.total === 0 &&
      debouncedSearchTerm === ""
    ) {
      // Avoid fetch if it's the very initial state before first fetch based on isAdmin
      // This condition might need adjustment based on exact initial state timing
    } else {
      console.log(
        `Fetching because debounced term changed: "${debouncedSearchTerm}"`
      );
      fetchData({
        current: 1, // Reset to page 1 when search term changes
        pageSize: pagination.pageSize,
        search: debouncedSearchTerm, // Use the debounced value
        sorter: sorter,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]); // This effect runs ONLY when the debounced term changes

  // --- Event Handlers ---

  const handleTableChange = (newPagination, tableFilters, newSorter) => {
    const fetchParams = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      search: debouncedSearchTerm, // Use the currently active (debounced) search term
      sorter: newSorter,
      // filters: tableFilters,
    };
    // It's usually better to call fetchData first, then update state if needed,
    // but updating state first is also fine here.
    // setPagination etc. states are updated within fetchData's success now.
    setSorter(newSorter); // Update sorter state locally
    // setFilters(tableFilters); // Update filter state if used
    fetchData(fetchParams);
  };

  // Handler for Search input CHANGE event
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value); // Update the raw search term immediately
  };

  // Handler for Search input ONSEARCH event (Enter or click search icon)
  const handleSearchSubmit = (value) => {
    console.log("Search submitted:", value);
    // Clear any pending debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    // Update both terms immediately and trigger fetch
    setSearchTerm(value);
    setDebouncedSearchTerm(value); // Trigger the fetch useEffect immediately
    // Note: The useEffect watching debouncedSearchTerm will handle the fetchData call.
    // No need to call fetchData directly here if the useEffect handles it.
  };

  const handleRefresh = () => {
    // Clear any pending debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    // Reset states
    setSearchTerm("");
    setDebouncedSearchTerm(""); // Trigger fetch with empty search
    setSorter({});
    // The useEffect watching debouncedSearchTerm will trigger the fetchData call.
  };

  // Add/Edit/Delete/Cancel Handlers (remain mostly the same, ensure they use correct state/fetchData)
  const showAddModal = () => {
    if (!isAdmin) {
      message.error("Unauthorized");
      return;
    }
    setEditingUser(null);
    setIsModalVisible(true);
  };

  const showEditModal = (user) => {
    if (!isAdmin) {
      message.error("Unauthorized");
      return;
    }
    setEditingUser(user);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingUser(null);
  };

  const handleFormSubmit = async (values) => {
    if (!isAdmin) {
      message.error("Unauthorized");
      return;
    }
    try {
      const dataToSend = { ...values };
      if (editingUser && !values.password) {
        delete dataToSend.password;
      }

      let successMessage = "";
      if (editingUser) {
        await updateUser(editingUser.id, dataToSend);
        successMessage = "User updated successfully!";
      } else {
        if (!values.password)
          throw new Error("Password is required for new users.");
        await addUser(dataToSend);
        successMessage = "User added successfully!";
      }
      message.success(successMessage);
      setIsModalVisible(false);
      setEditingUser(null);
      // Fetch data based on the *current* debounced search term after add/edit
      fetchData({
        current: 1, // Go to page 1 after add/edit usually makes sense
        pageSize: pagination.pageSize,
        search: debouncedSearchTerm, // Keep current search context
        sorter: sorter,
      });
    } catch (error) {
      message.error(`Operation failed: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) {
      message.error("Unauthorized");
      return;
    }
    if (id === currentUser?.id) {
      message.error("You cannot delete your own account.");
      return;
    }
    try {
      await deleteUser(id);
      message.success("User deleted successfully!");
      // Recalculate pagination potentially going back a page
      const newTotal = pagination.total - 1;
      let newCurrent = pagination.current;
      // Check if the deleted item was the last one on the current page
      if (newTotal > 0 && users.length === 1 && pagination.current > 1) {
        newCurrent = pagination.current - 1;
      }
      // Fetch data for the potentially adjusted current page, maintaining search/sort
      fetchData({
        current: newCurrent,
        pageSize: pagination.pageSize,
        search: debouncedSearchTerm, // Keep current search
        sorter: sorter,
      });
    } catch (error) {
      message.error(`Failed to delete user: ${error.message}`);
    }
  };

  // --- Table Columns Definition (remains the same) ---
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (name) => (
        <>
          <UserOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          {name}
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      render: (email) => (
        <>
          <MailOutlined style={{ marginRight: 8, color: "#52c41a" }} />
          {email}
        </>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: getRoleTag,
      sorter: true,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) =>
        isAdmin ? (
          <Space size="small">
            <Tooltip title="Edit User">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => showEditModal(record)}
                disabled={loading}
              />
            </Tooltip>
            <Tooltip
              title={
                record.id === currentUser?.id
                  ? "Cannot delete self"
                  : "Delete User"
              }
            >
              <Popconfirm
                title="Are you sure delete this user?"
                onConfirm={() => handleDelete(record.id)}
                okText="Yes"
                cancelText="No"
                disabled={loading || record.id === currentUser?.id}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  disabled={loading || record.id === currentUser?.id}
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        ) : null,
    },
  ];

  // --- Render Logic ---
  if (!isAdmin) {
    return (
      <Alert
        message="Access Denied"
        description="You do not have permission to manage users."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Header Controls */}
      <Space
        style={{
          marginBottom: 16,
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Space>
          <Search
            placeholder="Search users (name or email)..."
            // Use separate handlers for change and submit
            onChange={handleSearchInputChange} // Updates searchTerm on type
            onSearch={handleSearchSubmit} // Updates terms immediately on Enter/click
            value={searchTerm} // Controlled input
            style={{ width: 300 }}
            allowClear
            enterButton={<SearchOutlined />}
            // Loading indicator on search icon can be distracting during typing debounce
            // loading={loading && debouncedSearchTerm !== ''}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
          Add User
        </Button>
      </Space>

      {/* Users Table */}
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />

      {/* Add/Edit Modal */}
      <Modal
        title={editingUser ? "Edit User" : "Add New User"}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
        <UserForm
          key={editingUser ? `edit-${editingUser.id}` : "add"}
          initialValues={editingUser}
          onSubmit={handleFormSubmit}
          onCancel={handleCancel}
          loading={false} // Form itself doesn't need global loading, actions handle it
        />
      </Modal>
    </div>
  );
};

export default UserListPage;
